// invoiceStatements.js — column config for the Invoice Statements table view.
// Client-mode: the page filters via tabs/pills and passes the rows in, so the
// table's own toolbar (search/quick-filter) is disabled and filtering is off.
// We keep click-to-sort headers and the column show/hide cog menu.
import { createElement as h } from "react";
import { format, parseISO } from "date-fns";
import { deriveStatementStatus, statementStatusMeta } from "../constants/statementStatus";

// "Consolidated" multi-location statements are surfaced as "Enterprise" (matches the page).
const LOCATION_TYPE_LABELS = { Consolidated: "Enterprise" };
const formatLocationTypeLabel = (v) => LOCATION_TYPE_LABELS[v] ?? v ?? "";

const dayLabel = (iso) => {
   if (!iso) return "N/A";
   try { const d = parseISO(iso); return isNaN(d) ? "Invalid date" : format(d, "yyyy-MM-dd"); } catch { return "Invalid date"; }
};

// Same urgency colors as the tile ring (InvoiceStatementImage URGENCY_RING).
const URGENCY_COLOR = { OVERDUE: "#dc3545", DUE_THIS_WEEK: "#f59e0b", DUE_2_WEEKS: "#f59e0b" };

const dueDateCell = (v, row) => {
   const text = dayLabel(v);
   const color = URGENCY_COLOR[row?.terms_urgency_bucket];
   if (!color) return text;
   return h("span", { style: { color, fontWeight: 600 }, title: `Due ${text}` }, text);
};

// Status key for a table row, mapping the table's field names onto the shared
// derivation in ../constants/statementStatus.js (the tile grid uses the same one).
const rowStatusKey = (row) => deriveStatementStatus({
   statementState: row?.statement_state,
   invoiceCount: row?.invoice_count,
   matchCount: row?.invoice_state_match,
});

// Status renders as the SAME icon the tile grid shows — not a pill. Pills are for
// buttons/filter chips; status is an icon.
const statusCell = (_v, row) => {
   const m = statementStatusMeta(rowStatusKey(row));
   const matched = Number(row?.invoice_state_match) || 0;
   const total = Number(row?.invoice_count) || 0;
   return h(
      "span",
      { title: `${m.title} (${matched}/${total})`, style: { display: "inline-flex" } },
      h(m.icon, { color: m.color, size: 18 })
   );
};

export const invoiceStatementsTableConfig = {
   tableId: "invoiceStatements",
   rowIdField: "doc_part_id",
   defaultSort: { field: "sup_state_frmt_statement_date", dir: "desc" },
   showSearch: false,            // page header owns search/filtering
   gearInHeader: true,           // cog sits inline in the header row (Documents-style)
   stickyHeader: true,
   // Pins the <thead> below the page's sticky header. Tune this to the rendered bottom of
   // `.invoice-statements-sticky-header` (inspect it in Playwright); verify no gap/overlap.
   stickyHeaderTop: 150,
   columns: [
      {
         field: "sm_name", label: "Supplier", type: "string", sortable: true, filterable: false, defaultVisible: true,
         renderCell: (v) => h("a", { href: "#", className: "dt-record-link", onClick: (e) => e.preventDefault() }, v || "—"),
      },
      {
         // Real uploaded filename. The two list endpoints expose it under different keys:
         // getInvStatements.sz → doc_name (alias we added); getRecentDocuments.sz (the
         // "Recently Uploaded" tab) → doc_orig_name. Read either so both tabs populate.
         field: "doc_name", label: "Document", type: "string", sortable: true, filterable: false, defaultVisible: true,
         sortAccessor: (row) => row.doc_name ?? row.doc_orig_name ?? "",
         renderCell: (_v, row) => {
            const name = row.doc_name ?? row.doc_orig_name;
            return name == null || name === "" ? h("span", { style: { color: "#9aa0a6" } }, "—") : name;
         },
      },
      { field: "sup_state_frmt_statement_date", label: "Statement date", type: "date", sortable: true, filterable: false, defaultVisible: true },
      { field: "terms_due_date", label: "Due date", type: "date", sortable: true, filterable: false, defaultVisible: true, renderCell: dueDateCell },
      { field: "terms_label", label: "Terms", type: "string", sortable: true, filterable: false, defaultVisible: true,
        renderCell: (v) => (v == null || v === "" ? "—" : v) },
      {
         field: "matched", label: "Matched", type: "string", sortable: true, filterable: false, defaultVisible: true, align: "right",
         sortAccessor: (row) => Number(row.invoice_state_match) || 0,
         renderCell: (_v, row) => `${Number(row.invoice_state_match) || 0} / ${Number(row.invoice_count) || 0}`,
      },
      {
         field: "status", label: "Status", type: "string", sortable: true, filterable: false, defaultVisible: true, align: "center",
         sortAccessor: (row) => statementStatusMeta(rowStatusKey(row)).rank,
         renderCell: statusCell,
      },
      { field: "sup_state_type", label: "Location", type: "string", sortable: true, filterable: false, defaultVisible: false,
        renderCell: (v) => formatLocationTypeLabel(v) },
      { field: "cust_name", label: "Customer", type: "string", sortable: true, filterable: false, defaultVisible: false },
      { field: "has_active_follow_ups", label: "Follow-ups", type: "boolean", sortable: true, filterable: false, defaultVisible: false },
      { field: "invoice_partial_count", label: "Partial", type: "number", sortable: true, filterable: false, defaultVisible: false },
      { field: "terms_urgency_bucket", label: "Urgency", type: "string", sortable: true, filterable: false, defaultVisible: false },
      {
         field: "reconciliation_percent", label: "Reconciled %", type: "number", sortable: true, filterable: false, defaultVisible: false,
         renderCell: (v) => (v == null || v === "" ? "—" : `${Math.round(Number(v) * 100)}%`),
      },
      { field: "__actions", label: "", type: "string", sortable: false, filterable: false, defaultVisible: true, excludeFromDropdown: true, align: "right" },
   ],
};

export { formatLocationTypeLabel };
